<template>
    <div :class="classes">
        <div
            v-if="hasCaption || hasDescription"
            :class="element('header')"
        >
            <div
                v-if="hasCaption"
                :id="captionId"
                :class="captionClasses"
            >
                <slot name="caption">
                    {{ caption }}
                </slot>
            </div>
            <div
                v-if="hasDescription"
                :id="descriptionId"
                :class="element('description')"
            >
                <slot name="description">
                    {{ description }}
                </slot>
            </div>
        </div>

        <div
            ref="viewport"
            :class="element('viewport')"
            :tabindex="isOverflowing ? 0 : undefined"
            :role="isOverflowing && hasCaption ? 'region' : undefined"
            :aria-labelledby="isOverflowing && hasCaption ? captionId : undefined"
        >
            <table
                ref="table"
                :class="element('table')"
                :aria-labelledby="hasCaption ? captionId : undefined"
                :aria-describedby="hasDescription ? descriptionId : undefined"
            >
                <slot />
            </table>
        </div>

        <div
            v-if="$slots.footer"
            :class="element('footer')"
        >
            <slot name="footer" />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { KTableProps, KTableSlots } from './types'
import useBem from '@yeeroen/use-bem'
import { computed, onBeforeUnmount, onMounted, ref, useId, useSlots, useTemplateRef } from 'vue'

defineOptions({ name: 'KTable' })

const props = withDefaults(defineProps<KTableProps>(), {
    caption: undefined,
    description: undefined,
    captionVisibility: 'visible',
    density: 'comfortable',
    tone: 'neutral',
    variant: 'lined',
})

defineSlots<KTableSlots>()

const slots = useSlots()
const { block, element, modifier } = useBem('k-table')
const viewport = useTemplateRef<HTMLDivElement>('viewport')
const table = useTemplateRef<HTMLTableElement>('table')
const captionId = `k-table-caption-${useId()}`
const descriptionId = `k-table-description-${useId()}`
const isOverflowing = ref(false)

const hasCaption = computed(() => Boolean(props.caption || slots.caption))
const hasDescription = computed(() => Boolean(props.description || slots.description))
const classes = computed(() => block(
    modifier(props.density),
    modifier(props.tone),
    modifier(props.variant),
))
const captionClasses = computed(() => [
    element('caption'),
    props.captionVisibility === 'visually-hidden' && element('caption', 'visually-hidden'),
])

let resizeObserver: ResizeObserver | undefined

function measureOverflow() {
    if (!viewport.value)
        return

    isOverflowing.value = viewport.value.scrollWidth - viewport.value.clientWidth > 1
}

onMounted(() => {
    measureOverflow()
    window.addEventListener('resize', measureOverflow)

    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(measureOverflow)
        resizeObserver.observe(viewport.value!)
        resizeObserver.observe(table.value!)
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', measureOverflow)
    resizeObserver?.disconnect()
})
</script>

<style lang="scss">
@use './k-table.scss';
</style>
